import { At } from "@/icons/At.tsx";
import { Check } from "@/icons/Check.tsx";
import { CircleUser } from "@/icons/CircleUser.tsx";
import { Coin } from "@/icons/Coin.tsx";
import { MagnifyingGlass } from "@/icons/MagnifyingGlass.tsx";
import { UserSecret } from "@/icons/UserSecret.tsx";
import api from "@/lib/api.ts";
import { UserModel } from "@/models/user.model.ts";
import { getUsers } from "@/services/user.service.ts";
import useUserStore from "@/store/user.store.ts";
import { Error } from "@/ui/shared/Error.tsx";
import { Loading } from "@/ui/shared/Loading.tsx";
import {
  ActionIcon,
  Avatar,
  Badge,
  Card,
  CloseButton,
  Container,
  Flex,
  Group,
  Indicator,
  Input,
  Menu,
  NumberFormatter,
  Pagination,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.tz.setDefault("UTC");

export function Users() {
  const { impersonate } = useUserStore();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [page, setPage] = useState<number>(1);
  const [debouncedPage] = useDebouncedValue(page, 400);
  const params = {
    page: debouncedPage,
    limit: 20,
    sort: "-created_at",
    "filter[search]": debouncedSearch,
    include: "personalAccessTokens",
  };

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ["users", params],
    queryFn: () => getUsers(params),
    retry: 1,
  });

  const users: UserModel[] =
    data?.data.map((user) => {
      const token = user.personal_access_tokens.data[0];

      if (!token) {
        return user;
      }

      if (!token.last_used_at) {
        return user;
      }

      const updatedUser = Object.assign({}, user);

      updatedUser.last_seen_at = dayjs.utc(token.last_used_at).fromNow();

      const lastUsedAt = dayjs.utc(token.last_used_at);
      const diff = lastUsedAt.diff(dayjs.utc(), "minutes");

      updatedUser.is_active = Math.abs(diff) < 15;
      return updatedUser;
    }) || [];
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    setTotalUsers(data?.meta.total ?? 0);
  }, [data]);

  if (isError) {
    return <Error />;
  }

  const handleImpersonation = (user: UserModel) => {
    navigate("/");
    impersonate(user);
  };

  const topUpMutation = useMutation({
    mutationFn: (uuid: string) => {
      return api.post(`/users/${uuid}/top-up`);
    },
    onSuccess: () => {
      refetch();
    },
  });

  const couponCodeMutation = useMutation({
    mutationFn: (uuid: string) => {
      return api.post(`/users/${uuid}/emails/coupon-code`);
    },
    onSuccess: () => {
      refetch();
    },
  });

  const rows = users.map((user) => (
    <Table.Tr key={user.id}>
      <Table.Td>{user.id}</Table.Td>
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <Indicator
            color={!user.is_active ? "red.6" : "green.4"}
            withBorder
            processing={user.is_active}
            zIndex={5}
          >
            <Avatar size={40} src={user.avatar} radius={4}>
              <CircleUser size={24} />
            </Avatar>
          </Indicator>
          <div>
            <Text fz="sm" fw={500}>
              {user.name}
            </Text>
            <Text fz="xs" c="dimmed">
              {user.email}
            </Text>
          </div>
        </Group>
      </Table.Td>
      <Table.Td>
        <NumberFormatter value={user.credits} thousandSeparator />
      </Table.Td>
      <Table.Td>
        {user.email_promo_1_sent_at && (
          <Badge
            leftSection={<Check size={10} />}
            variant="light"
            color="green"
          >
            Promo 1
          </Badge>
        )}
      </Table.Td>
      <Table.Td>{user.last_seen_at}</Table.Td>
      <Table.Td>
        <Flex gap={8}>
          <ActionIcon
            variant="outline"
            color="pink"
            onClick={() => handleImpersonation(user)}
            size="md"
          >
            <UserSecret size={12} />
          </ActionIcon>

          <ActionIcon
            disabled={topUpMutation.isPending}
            variant="outline"
            color="green"
            onClick={() => topUpMutation.mutate(user.uuid)}
            size="md"
          >
            <Coin size={12} />
          </ActionIcon>

          <Menu
            position="bottom-end"
            transitionProps={{ transition: "pop-top-right" }}
            withinPortal
            withArrow
            arrowPosition="center"
          >
            <Menu.Target>
              <ActionIcon
                disabled={couponCodeMutation.isPending}
                variant="outline"
                color="violet"
                size="md"
              >
                <At size={12} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>Send notifications</Menu.Label>

              <Menu.Item
                disabled={user.email_promo_1_sent_at !== null}
                onClick={() => couponCodeMutation.mutate(user.uuid)}
              >
                Coupon code
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Container size="xl" px={0} className="flex flex-col gap-5">
      <div className="flex flex-col md:flex-row gap-2 w-full justify-start md:justify-between md:items-center">
        <Title order={3} fw={500}>
          Users ({totalUsers || 0})
        </Title>

        <TextInput
          miw={300}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          leftSection={<MagnifyingGlass size={14} />}
          placeholder="Search users..."
          rightSection={
            search && (
              <CloseButton
                className="cursor-pointer"
                aria-label="Clear input"
                onClick={() => setSearch("")}
              />
            )
          }
        />
      </div>

      {isFetching && (
        <div className="flex flex-col w-full justify-center mb-4">
          <Loading />
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-3">
        <Card withBorder w="100%">
          <Card.Section>
            <Table.ScrollContainer type="native" minWidth={500} pb={0}>
              <Table
                tabularNums
                striped
                highlightOnHover
                withColumnBorders
                horizontalSpacing="sm"
                verticalSpacing="sm"
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>ID</Table.Th>
                    <Table.Th>User</Table.Th>
                    <Table.Th>Credits</Table.Th>
                    <Table.Th>Emails</Table.Th>
                    <Table.Th>Last activity</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows}</Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Card.Section>
        </Card>
      </div>

      {data && (
        <div className="flex items-center justify-between gap-2">
          <Pagination
            value={page}
            onChange={setPage}
            total={data.meta.last_page}
            radius="md"
            withEdges
          />

          <div className="flex flex-row flex-nowrap gap-2 items-center">
            <Text>Page:</Text>
            <Input
              w={100}
              className="text-center"
              type="number"
              value={page}
              onChange={(event) => setPage(+event.currentTarget.value)}
            />
          </div>
        </div>
      )}
    </Container>
  );
}
